import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom';

import {Assembly, GibsonParameters, GoldenGateParameters} from 'assembly/AssemblyModels';
import {AssemblyPreviewProps} from 'assembly/tabs/primer-assembly/AssemblyPreview';
import PrimerAssemblyTabState, {
  PrimerAssemblyTabRowState,
} from 'assembly/tabs/primer-assembly/backbone/PrimerAssemblyTabState';
import {DesignParametersProps} from 'assembly/tabs/primer-assembly/DesignParameters';
import {MismatchAttrs} from 'assembly/tabs/primer-assembly/Mismatches';
import PrimerAssemblyTab, {
  MaybeAssemblyPreview,
  MaybeMismatches,
} from 'assembly/tabs/primer-assembly/PrimerAssemblyTab';
import {MaybeEnzyme, PrimerRowAttrs} from 'assembly/tabs/primer-assembly/PrimerRows';
import {StickyEndsProps} from 'assembly/tabs/primer-assembly/StickyEnds';
import {assert} from 'Asserts';
import {PrimerAssemblyMismatch} from 'components/PrimerAssemblyMismatches';
import {Sequence} from 'components/Sequence';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as sequtil from 'molbio/SeqUtil';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {ElementMouseEvent} from 'util/Types';

function assertNonNullishAssembly(assembly: Assembly | undefined): asserts assembly is Assembly {
  assert(assembly != null, 'Found unexpected nullish assembly');
}

function assertPrimerAssemblyType(
  assemblyType: ASSEMBLY_TYPE | null
): asserts assemblyType is ASSEMBLY_TYPE.GOLDEN_GATE | ASSEMBLY_TYPE.GIBSON {
  assert(assemblyType != null, 'Found unexpected nullish assembly type');
  assert(assemblyType !== ASSEMBLY_TYPE.DIGEST, 'Found unexpected digest assembly in primer assembly tab');
}

export default class TabView extends Backbone.View<PrimerAssemblyTabState> {
  override className!: string;
  sequence!: Sequence;
  showBinding!: boolean;
  showHomology!: boolean;

  static initClass(): void {
    this.prototype.className = 'primer-assembly-tab-results buffer-tab';
    this.prototype.showBinding = true;
    this.prototype.showHomology = true;
  }

  override initialize(): void {
    this.sequence = this.model.get('sequence');
    this.listenTo(
      this.sequence._sequence(),
      'change:bases change:finalized change:fragmentResults change:primerPairs',
      () => {
        this.render();
      }
    );
    this.listenTo(this.model.get('rows'), 'change reset', () => {
      this.render();
    });
    this.listenTo(this.model, 'change:showPrintableSummary', () => {
      this.render();
    });
    this.listenTo(this.sequence.primerAssemblyMismatches(), 'reset', () => {
      this.render();
    });
    this.listenTo(this.sequence.sequencePrimers(), 'change', () => {
      this.render();
    });
  }

  _getAssemblyPreviewProps(): MaybeAssemblyPreview {
    const showPrintableSummary = this.model.showPrintableSummary();
    const assemblyPreviewProps: AssemblyPreviewProps = {
      fragmentSequenceNames: this.sequence
        .fragmentResults()
        .map((fragmentResult) => fragmentResult.parent()?.name),
      // @ts-expect-error snapshot_url is not an attribute on BioJSSequence per typedef, but we populate
      // it when getting the printable summary and JavaScript just lets us
      snapshotURL: this.sequence._sequence().get('snapshot_url') ?? '',
    };
    return showPrintableSummary
      ? {
          assemblyPreviewProps,
          showPrintableSummary,
        }
      : {showPrintableSummary};
  }

  _getEnzymeProps(): MaybeEnzyme {
    const assembly = this.sequence._sequence().assembly();
    assertNonNullishAssembly(assembly);
    const assemblyType = assembly.method();
    assertPrimerAssemblyType(assemblyType);
    const isGoldenGate = assemblyType === ASSEMBLY_TYPE.GOLDEN_GATE;
    const enzyme = assembly.enzyme();
    return isGoldenGate ? {enzyme, isGoldenGate} : {isGoldenGate};
  }

  _getMismatchesProps(): MaybeMismatches {
    const finalized = this.sequence.finalized()!;
    const mismatches: Array<MismatchAttrs> = this.sequence
      .primerAssemblyMismatches()
      .map((mismatch: PrimerAssemblyMismatch) => ({
        fragmentResultColor: this.model.lighten(mismatch.get('fragmentResultColor')),
        fragmentSequenceName: mismatch.get('fragmentSequenceName'),
        fragmentSequenceID: mismatch.get('fragmentSequenceID'),
        newBase: mismatch.get('newBase'),
        onClickFragmentSequenceLink: () => {
          FileBrowserActions.openFile(mismatch.get('fragmentSequenceID'));
        },
        originalBase: mismatch.get('originalBase'),
        pos: sequtil.modelToDisplay(mismatch.get('pos'), this.model.get('sequence').startIndex()),
        showPrintableSummary: this.model.showPrintableSummary(),
      }));
    return finalized ? {finalized} : {finalized, mismatches};
  }

  _getPrimersProps(): Array<PrimerRowAttrs> {
    return this.model.get('rows').map((rowState: PrimerAssemblyTabRowState) => {
      const primer = this.sequence.sequencePrimers().get(rowState.get('id'));
      return {
        baseMatchingStatuses: primer.get('_primer').baseMatchingStatuses(this.sequence.get('_sequence')),
        binding: rowState.get('binding'),
        dg: rowState.get('dg'),
        fragmentResult: this.sequence.fragmentResults().at(rowState.get('fragmentResultIndex')),
        fragmentResultColor: this.model.lighten(rowState.get('fragmentResultColor')),
        misprime: rowState.get('misprime'),
        onClickDg: () => {
          rowState.get('dgHandler')();
        },
        onClickMisprime: () => {
          rowState.get('misprimeHandler')();
        },
        onClickPair: () => {
          this.sequence.selectPrimerPair(rowState.get('pairId'));
        },
        onClickPrimer: (e: ElementMouseEvent) => {
          primer.dblClick(this.sequence, e.shiftKey);
        },
        overhang: rowState.get('overhang'),
        pairId: rowState.get('pairId'),
        pairedId: rowState.get('pairedId'),
        primerBases: primer.bases(),
        primerId: rowState.get('id'),
        primerName: primer.name(),
        tmAnneal: rowState.get('tmAnneal'),
        tmDiff: rowState.get('tmDiff'),
        tmWhole: rowState.get('tmWhole'),
      };
    });
  }

  /**
   * Gets React props for rendering a sticky ends table. Implemented by sticky-ends-aware subclasses.
   */
  _getStickyEndsProps(): StickyEndsProps | null {
    return null;
  }

  override remove(): Backbone.View<PrimerAssemblyTabState> {
    ReactDOM.unmountComponentAtNode(this.$el[0]);
    return super.remove();
  }

  _renderTab(node: Element): void {
    // calculating this out here to avoid Typescript Generic Hell, which we would encounter if we had
    // made it a function
    const assembly = this.sequence._sequence().assembly();
    assertNonNullishAssembly(assembly);
    const assemblyType = assembly.method();
    assertPrimerAssemblyType(assemblyType);
    const designParametersProps: DesignParametersProps<typeof assemblyType> = {
      assemblyType,
      // unfortunately it is extremely obnoxious to check whether something is empty object in a typesafe way
      parameters: assembly.parameters() as GibsonParameters | GoldenGateParameters,
    };

    renderToDOM(
      <PrimerAssemblyTab
        {...this._getAssemblyPreviewProps()}
        {...this._getEnzymeProps()}
        {...this._getMismatchesProps()}
        designParametersProps={designParametersProps}
        primerRows={this._getPrimersProps()}
        showBinding={this.showBinding}
        showHomology={this.showHomology}
        stickyEndsProps={this._getStickyEndsProps()}
      />,
      node
    );
  }

  override render(): this {
    this._renderTab(this.$el[0]);
    return this;
  }
}

TabView.initClass();
